/**
 * PlayerContext - Context React pour gérer l'état du jeu
 * Identique à Flutter PlayerController
 */

import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {Platform} from 'react-native';
import {PlayerStep} from '../models/PlayerStep';
import {GameState, createGameState, GameStateStatus} from '../models/GameState';
import {PlayerAction} from '../models/PlayerAction';
import {PlayerPath} from '../models/PlayerPath';
import {PlayerAdventure} from '../models/PlayerAdventure';
import {createGameData} from '../models/GameData';
import {GameDataExtraField} from '../models/GameDataExtraField';
import playerService from '../services/playerService';
import gameLogicService from '../services/gameLogicService';
import gameStateService from '../services/gameStateService';
import adventureService from '../services/adventureService';
import GameStateHelper from '../helpers/GameStateHelper';
import getSupabaseClient from '../services/supabaseService';
import storageService from '../services/storageService';

interface PlayerContextType {
  currentStep: PlayerStep | null;
  gameState: GameState | null;
  currentAdventure: PlayerAdventure | null;
  isLoading: boolean;
  isContextReady: boolean; // Flag pour indiquer que le contexte est vraiment prêt (gameState + currentStep)
  currentSlot: number;
  editorMode: boolean;
  setEditorMode: (editorMode: boolean) => void;
  goToPreviousStep: () => Promise<void>;
  // Méthodes
  initGame: (adventureURI: string, slot?: number) => Promise<void>;
  initTestGame: (adventureURI: string, stepNumber: number) => Promise<void>;
  startSlot: (slot: number, adventureURI?: string) => Promise<void>;
  loadGame: (adventureURI: string, slot: number) => Promise<void>;
  choosePath: (pathIndex: number) => Promise<void>;
  executeAction: (actionTypeName: string, params?: {[key: string]: any}) => Promise<void>;
  refreshCurrentStep: () => Promise<void>;
  resetGameState: () => void;
  setGameState: (gameState: GameState) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [currentStep, setCurrentStep] = useState<PlayerStep | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [currentAdventure, setCurrentAdventure] = useState<PlayerAdventure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isContextReady, setIsContextReady] = useState(false); // Flag de synchronisation
  const [currentSlot, setCurrentSlot] = useState(1);
  const [editorMode, setEditorModeState] = useState(false);

  // Charger editorMode depuis le storage au démarrage (uniquement en mode normal, pas en mode édition/test)
  useEffect(() => {
    const loadEditorMode = async () => {
      try {
        // En mode édition/test (popup), ne pas charger le editorMode depuis le storage
        // Il doit toujours être désactivé par défaut
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.opener) {
          console.log('[PlayerContext] In editing mode, editor mode disabled by default');
          setEditorModeState(false);
          return;
        }
        
        await storageService.init();
        const savedEditorMode = await storageService.getItem('editor_mode');
        if (savedEditorMode === 'true') {
          setEditorModeState(true);
        }
      } catch (error) {
        console.error('[PlayerContext] Failed to load editor mode preference:', error);
      }
    };
    loadEditorMode();
  }, []);

  // Wrapper pour setEditorMode qui sauvegarde aussi dans le storage
  const setEditorMode = async (value: boolean) => {
    setEditorModeState(value);
    try {
      await storageService.setItem('editor_mode', value ? 'true' : 'false');
      console.log(`[PlayerContext] Editor mode ${value ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('[PlayerContext] Failed to save editor mode preference:', error);
    }
  };

  // Sauvegarder editorMode dans le storage quand il change
  useEffect(() => {
    const saveEditorMode = async () => {
      try {
        await storageService.init();
        await storageService.setItem('editor_mode', editorMode ? 'true' : 'false');
      } catch (error) {
        console.error('[PlayerContext] Failed to save editor mode preference:', error);
      }
    };
    saveEditorMode();
  }, [editorMode]);

  // Mettre à jour isContextReady quand gameState et currentStep sont tous les deux prêts
  // Ce flag permet de s'assurer que le contexte est vraiment propagé avant la navigation
  useEffect(() => {
    const ready = !!(gameState && currentStep && !isLoading);
    setIsContextReady(prevReady => {
      if (prevReady !== ready) {
        console.log(`[PlayerContext] isContextReady changed: ${prevReady} -> ${ready} (gameState: ${!!gameState}, currentStep: ${!!currentStep}, isLoading: ${isLoading})`);
        return ready;
      }
      return prevReady;
    });
  }, [gameState, currentStep, isLoading]);

  /**
   * Initialise le jeu pour une aventure
   * Identique à Flutter PlayerController.initGame
   */
  const initGame = async (adventureURI: string, slot: number = 1) => {
    console.log(`[PlayerContext] Initializing game:`);
    console.log(`[PlayerContext] - adventureURI: ${adventureURI}`);
    console.log(`[PlayerContext] - slot: ${slot}`);

    setIsLoading(true);
    try {
      // Charger l'aventure complète
      const adventure = await adventureService.loadPlayerAdventure(adventureURI);
      setCurrentAdventure(adventure);
      gameLogicService.setCurrentAdventure(adventure);

      // Vérifier si un gameState existe déjà
      let state = await playerService.getGameState(adventureURI, slot);

      // Si pas de gameState, en créer un nouveau
      if (!state) {
        const startNumero = adventure.startNumero || 1;
        const adventureType = adventure.type || 'SIMPLE';

        // Initialiser les extraFields d'aventure selon le type de système
        const adventureExtraFields = GameStateHelper.initAdventureExtraFields(adventure.extraFields, adventureType);
        const gameData = createGameData(startNumero, startNumero);
        gameData.adventureExtraFields = adventureExtraFields;

        // Récupérer userId depuis Supabase
        const supabase = getSupabaseClient();
        const {data: {session}} = await supabase.auth.getSession();
        const userId = session?.user?.id || 'temp-user';
        
        if (!adventureURI) {
          throw new Error('No adventure URI available for creating game state');
        }
        
        state = createGameState(adventureURI, slot, gameData, userId);
        state.data.recordedPaths = startNumero.toString();
        state.data.nbDifferentSteps = 1;

        await gameStateService.createGameState(adventureURI, slot, state.data);
      }

      setGameState(state);
      gameLogicService.setGameState(state);
      setCurrentSlot(slot);

      // Charger l'étape actuelle
      const step = await playerService.getCurrentStep(state);
      if (step) {
        setCurrentStep(step);
      }

      console.log(`[PlayerContext] Game initialized successfully`);
    } catch (error) {
      console.error('[PlayerContext] Error initializing game:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initialise le jeu en mode test pour une étape spécifique (routing /test/:adventureURI/step/:stepNumber)
   * Portage de Flutter PlayerController.initTestGame
   */
  const initTestGame = async (adventureURI: string, stepNumber: number) => {
    console.log('[PlayerContext] Initializing test game:');
    console.log(`[PlayerContext] - adventureURI: ${adventureURI}`);
    console.log(`[PlayerContext] - stepNumber: ${stepNumber}`);

    setIsLoading(true);
    try {
      // 1) Charger l'aventure complète depuis l'API éditeur UNIQUEMENT
      // Identique à Flutter AdventureServiceEditing.loadPlayerAdventure
      // PAS de fallback vers la DB locale en mode édition/test
      const adventure = await adventureService.loadEditingPlayerAdventure(adventureURI);
      console.log(
        `[PlayerContext] Test adventure loaded from EDITOR API: type=${adventure.type}`,
      );

      setCurrentAdventure(adventure);
      gameLogicService.setCurrentAdventure(adventure);

      // 2) Charger la step spécifique depuis l'API éditeur UNIQUEMENT
      // Identique à Flutter AdventureServiceEditing.loadPlayerStep
      // PAS de fallback vers la DB locale en mode édition/test
      const step = await adventureService.loadEditingPlayerStep(adventureURI, stepNumber);

      // 3) Créer un GameState en mémoire sans persistance (userId de test)
      const adventureType = adventure.type || 'SIMPLE';
      let adventureExtraFields: GameDataExtraField[] = [];

      // Initialiser les extraFields : utilise ceux de l'aventure si disponibles, sinon fallback sur les defaults
      adventureExtraFields = GameStateHelper.initAdventureExtraFields(adventure.extraFields, adventureType);

      const gameData = createGameData(stepNumber, stepNumber);
      gameData.adventureExtraFields = adventureExtraFields;
      gameData.recordedPaths = stepNumber.toString();
      gameData.nbDifferentSteps = 1;

      const state = createGameState(adventureURI, 1, gameData, 'test-user');

      // Note: Le backup sera créé dans choosePath si backupAllowed est true
      // Pas besoin de créer le backup ici dans initTestGame

      // IMPORTANT: Appeler getGameStep pour traiter les événements de l'étape (notebook, etc.)
      // Cela va aussi mettre à jour recordedPaths et traiter les attributs
      const gameStep = await gameLogicService.getGameStep(
        state,
        step,
        'init',
        {doEventsAndUpdateRecordedPaths: true},
      );

      // Utiliser le gameState depuis gameStep qui contient les modifications (notebook, etc.)
      const finalState = gameStep.gameState || state;

      // Attacher le gameState à la step pour que StepRouter puisse l'utiliser
      step.gameState = finalState;

      console.log(`[PlayerContext] Test game state created for test route`);

      setGameState(finalState);
      gameLogicService.setGameState(finalState);
      setCurrentSlot(1);
      setCurrentStep(gameStep);
      console.log(
        `[PlayerContext] Test game initialized successfully at step ${step.numero}`,
      );
    } catch (error) {
      console.error('[PlayerContext] Error initializing test game:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Démarre une partie dans un slot
   * Identique à Flutter PlayerController.startSlot
   */
  const startSlot = async (slot: number, adventureURI?: string) => {
    console.log(`[PlayerContext] ===== STARTING SLOT ${slot} =====`);
    console.log(`[PlayerContext] startSlot - adventureURI param: ${adventureURI}`);

    // Utiliser adventureURI passé en paramètre si fourni, sinon utiliser currentAdventure
    let finalAdventureURI = adventureURI;
    
    if (!finalAdventureURI) {
      if (!currentAdventure) {
        throw new Error('No current adventure loaded and no adventureURI provided');
      }
      finalAdventureURI = currentAdventure.uniqueURI;
    }

    setIsLoading(true);
    try {

      // Charger l'aventure si nécessaire pour obtenir le type et initialiser le gameState
      let adventureForType = currentAdventure;
      if (!adventureForType && finalAdventureURI) {
        adventureForType = await adventureService.loadPlayerAdventure(finalAdventureURI);
        if (!currentAdventure) {
          setCurrentAdventure(adventureForType);
          gameLogicService.setCurrentAdventure(adventureForType);
        }
      }

      // Vérifier si un état de jeu existe déjà pour ce slot
      const existingState = await playerService.getGameState(finalAdventureURI, slot);

      let state: GameState;

      if (existingState) {
        // Utiliser l'état existant
        state = existingState;
        console.log(`[PlayerContext] Using existing game state for slot ${slot}`);
        
        // Vérifier si les extraFields d'aventure sont vides et les initialiser si nécessaire
        const adventureType = adventureForType?.type || currentAdventure?.type || 'SIMPLE';
        if (state.data.adventureExtraFields.length === 0) {
          console.log(`[PlayerContext] Existing gameState has no adventureExtraFields, initializing...`);
          let adventureExtraFields: GameDataExtraField[] = [];
          const adventure = adventureForType || currentAdventure;
          // Initialiser les extraFields : utilise ceux de l'aventure si disponibles, sinon fallback sur les defaults
          adventureExtraFields = GameStateHelper.initAdventureExtraFields(adventure?.extraFields, adventureType);
          if (adventureExtraFields.length > 0) {
            state.data.adventureExtraFields = adventureExtraFields;
            await gameStateService.updateGameState(state);
            console.log(`[PlayerContext] Initialized ${adventureExtraFields.length} adventureExtraFields and updated gameState`);
          }
        }
      } else {
        // Créer un nouvel état de jeu
        // adventureForType est déjà chargé ci-dessus
        if (!adventureForType) {
          throw new Error('No adventure available for slot initialization');
        }
        
        const startNumero = adventureForType.startNumero || 1;
        const adventureType = adventureForType.type || 'SIMPLE';

        // Initialiser les extraFields d'aventure à partir des extraFields de l'aventure
        // Si l'aventure a des extraFields définis, les utiliser, sinon fallback sur le type
        let adventureExtraFields: GameDataExtraField[] = [];
        if (adventureForType.extraFields && adventureForType.extraFields.length > 0) {
          // Les extraFields du content JSON sont des objets avec value et type
          // Initialiser les extraFields : utilise ceux de l'aventure si disponibles, sinon fallback sur les defaults
          adventureExtraFields = GameStateHelper.initAdventureExtraFields(adventureForType.extraFields, adventureType);
        } else {
          // Pas d'extraFields dans l'aventure, utiliser le fallback basé sur le type
          adventureExtraFields = GameStateHelper.initAdventureExtraFields(undefined, adventureType);
        }
        console.log(`[PlayerContext] Initializing ${adventureExtraFields.length} adventure extraFields for type ${adventureType}`);

        const gameData = createGameData(startNumero, startNumero);
        gameData.adventureExtraFields = adventureExtraFields;

        // Récupérer userId depuis Supabase
        const supabase = getSupabaseClient();
        const {data: {session}} = await supabase.auth.getSession();
        const userId = session?.user?.id || 'temp-user';
        
        state = createGameState(finalAdventureURI, slot, gameData, userId);
        state.data.recordedPaths = startNumero.toString();
        state.data.nbDifferentSteps = 1;

        await gameStateService.createGameState(finalAdventureURI, slot, state.data);
        console.log(`[PlayerContext] Created new game state for slot ${slot} with ${adventureExtraFields.length} extraFields`);
      }

      console.log(`[PlayerContext] Setting gameState and currentStep...`);
      setGameState(state);
      gameLogicService.setGameState(state);
      setCurrentSlot(slot);
      console.log(`[PlayerContext] gameState set, state.uniqueURI: ${state.uniqueURI}, state.slot: ${state.slot}`);

      // Charger l'étape actuelle
      const step = await playerService.getCurrentStep(state);
      if (step) {
        console.log(`[PlayerContext] Setting currentStep, step.numero: ${step.numero}`);
        setCurrentStep(step);
      } else {
        console.warn(`[PlayerContext] No step loaded for state`);
      }

      // Attendre que React ait mis à jour le contexte AVANT de mettre isLoading à false
      // Les setState sont asynchrones, donc on attend quelques frames de rendu
      // pour s'assurer que les mises à jour sont propagées aux composants
      await new Promise(resolve => {
        // Utiliser requestAnimationFrame pour attendre plusieurs cycles de rendu
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Après 3 frames, le contexte devrait être mis à jour
              console.log(`[PlayerContext] After 3 frames, checking context state...`);
              resolve(undefined);
            });
          });
        });
      });

      console.log(`[PlayerContext] Slot ${slot} started successfully`);
      
      // Mettre isLoading à false APRÈS avoir attendu que le contexte soit propagé
      // Le flag isContextReady sera mis à jour automatiquement par le useEffect
      // qui surveille gameState et currentStep
      setIsLoading(false);
    } catch (error) {
      console.error(`[PlayerContext] Error starting slot ${slot}:`, error);
      setIsLoading(false); // S'assurer que isLoading est réinitialisé en cas d'erreur
      throw error;
    }
  };

  /**
   * Charge une partie existante
   */
  const loadGame = async (adventureURI: string, slot: number) => {
    setIsLoading(true);
    try {
      await initGame(adventureURI, slot);
    } catch (error) {
      console.error('[PlayerContext] Error loading game:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Choisit un chemin (par index)
   * Identique à Flutter PlayerController.choosePath
   */
  const choosePath = async (pathIndex: number): Promise<void> => {
    if (!gameState) {
      throw new Error('No game state loaded');
    }
    
    if (!currentStep) {
      throw new Error('No current step loaded');
    }

    console.log(`[PlayerContext] ===== choosePath CALLED =====`);
    console.log(`[PlayerContext] pathIndex: ${pathIndex}`);
    console.log(`[PlayerContext] editorMode: ${editorMode}`);
    console.log(`[PlayerContext] currentStep.numero: ${currentStep.numero}`);
    console.log(`[PlayerContext] currentStep.paths.length: ${currentStep.paths.length}`);
    
    // En mode éditeur, utiliser allPaths (tous les paths originaux) pour la validation
    // Sinon, utiliser paths (paths filtrés)
    const pathsToValidate = editorMode && currentStep.allPaths 
      ? currentStep.allPaths 
      : currentStep.paths;
    
    console.log(`[PlayerContext] Using ${editorMode && currentStep.allPaths ? 'allPaths' : 'paths'} for validation (length: ${pathsToValidate.length})`);
    
    // VÉRIFICATION CRITIQUE : S'assurer que le path à l'index existe dans le currentStep du contexte
    if (pathIndex < 0 || pathIndex >= pathsToValidate.length) {
      console.error(`[PlayerContext] Invalid path index: ${pathIndex} (valid range: 0-${pathsToValidate.length - 1})`);
      throw new Error(`Invalid path index: ${pathIndex}`);
    }
    
    const pathFromContext = pathsToValidate[pathIndex];
    console.log(`[PlayerContext] Path from context at index ${pathIndex}:`, {
      id: pathFromContext.id,
      description: pathFromContext.description?.substring(0, 50),
      child: pathFromContext.child,
      conditions: pathFromContext.conditions,
    });
    
    console.log(`[PlayerContext] gameState snapshot:`, {
      uniqueURI: gameState.uniqueURI,
      slot: gameState.slot,
      currentStepNumber: gameState.data.currentStepNumber,
      objects: gameState.data.objects?.map(o => ({id: o.attributeId, dropped: o.dropped, value: o.value})) || [],
      skills: gameState.data.skills?.map(s => ({id: s.attributeId, value: s.value})) || [],
      adventureExtraFields: gameState.data.adventureExtraFields?.map(f => ({type: f.type, currentValue: f.currentValue, initValue: f.initValue})) || [],
    });

    setIsLoading(true);
    try {
      // Créer un backup AVANT de choisir le path si backupAllowed est true
      // Comme en Java : le backup est créé avant chaque choosePath pour permettre le "rembobiner"
      if (currentAdventure?.isBackupAllowed && !gameState.hasBackup) {
        // Créer un backup en mémoire (copie du GameState avec état PLAYING)
        // Le backup représente l'état AVANT de choisir le path
        const backup = JSON.parse(JSON.stringify(gameState));
        backup.state = GameStateStatus.PLAYING;
        backup.id = `backup-${gameState.id || Date.now()}`;
        backup.idApi = backup.id;
        gameState.backup = backup;
        gameState.hasBackup = true;
        console.log('[PlayerContext] Created backup before choosePath', {
          hasBackup: gameState.hasBackup,
          backupId: backup.id,
          fromStep: gameState.data.currentStepNumber,
        });
      }

      // IMPORTANT : Utiliser le currentStep du contexte au lieu de le recharger
      // Cela garantit que le path à l'index correspond bien au path affiché
      // Passer aussi le currentStep pour éviter de le recharger dans playerService
      const step = await gameLogicService.choosePath(gameState, pathIndex, editorMode, currentStep);

      // IMPORTANT: Récupérer le gameState mis à jour depuis gameLogicService
      // car getGameStep modifie le gameState (ajoute compétences, objets, etc.)
      // et le sauvegarde. Le gameState original peut ne pas avoir toutes les modifications.
      const updatedStateFromService = gameLogicService.getGameState();
      if (!updatedStateFromService) {
        throw new Error('GameState not found in gameLogicService after path choice');
      }

      // Créer une nouvelle référence profonde pour que React détecte le changement
      const updatedState = JSON.parse(JSON.stringify(updatedStateFromService));
      
      setGameState(updatedState);
      gameLogicService.setGameState(updatedState);

      setCurrentStep(step);
      console.log(`[PlayerContext] Path chosen successfully, new step: ${step.numero}`);
      console.log(`[PlayerContext] Objects in inventory: ${updatedState.data.objects?.length || 0}`);
      console.log(`[PlayerContext] Skills: ${updatedState.data.skills?.length || 0}`);
      console.log(`[PlayerContext] Butterfly effects: ${updatedState.data.butterflyEffects?.length || 0}`);
    } catch (error) {
      console.error('[PlayerContext] Error choosing path:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Exécute une action (par typeName)
   * Identique à Flutter PlayerController.executeAction
   */
  const executeAction = async (
    actionTypeName: string,
    params: {[key: string]: any} = {},
  ) => {
    if (!gameState) {
      throw new Error('No game state loaded');
    }

    console.log(`[PlayerContext] ===== executeAction CALLED =====`);
    console.log(`[PlayerContext] actionTypeName: ${actionTypeName}`);
    console.log(`[PlayerContext] params:`, params);

    setIsLoading(true);
    try {
      // Le gameLogicService.executeAction modifie le gameState par référence
      // et le sauvegarde en base via getGameStep qui appelle updateGameState
      const step = await gameLogicService.executeAction(gameState, actionTypeName, params);

      // IMPORTANT: Récupérer le gameState mis à jour depuis gameLogicService
      // car getGameStep modifie le gameState (ajoute compétences, objets, etc.)
      // et le sauvegarde. Le gameState original peut ne pas avoir toutes les modifications.
      const updatedStateFromService = gameLogicService.getGameState();
      if (!updatedStateFromService) {
        throw new Error('GameState not found in gameLogicService after action');
      }

      // Créer une nouvelle référence profonde pour que React détecte le changement
      const updatedState = JSON.parse(JSON.stringify(updatedStateFromService));
      
      setGameState(updatedState);
      gameLogicService.setGameState(updatedState);

      setCurrentStep(step);
      
      console.log(`[PlayerContext] Action executed successfully`);
      console.log(`[PlayerContext] Objects in inventory: ${updatedState.data.objects?.length || 0}`);
      console.log(`[PlayerContext] Skills: ${updatedState.data.skills?.length || 0}`);
      console.log(`[PlayerContext] Butterfly effects: ${updatedState.data.butterflyEffects?.length || 0}`);
      console.log(`[PlayerContext] Current step: ${step.numero}`);
    } catch (error) {
      console.error('[PlayerContext] Error executing action:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Rafraîchit l'étape actuelle
   */
  const refreshCurrentStep = async () => {
    if (!gameState) {
      return;
    }

    try {
      const step = await playerService.getCurrentStep(gameState);
      if (step) {
        setCurrentStep(step);
      }
    } catch (error) {
      console.error('[PlayerContext] Error refreshing current step:', error);
    }
  };

  /**
   * Réinitialise le gameState et currentStep (pour retourner au menu)
   */
  const resetGameState = () => {
    console.log('[PlayerContext] Resetting game state and current step');
    setGameState(null);
    setCurrentStep(null);
    setIsLoading(false); // Réinitialiser aussi isLoading pour éviter les états bloqués
    setIsContextReady(false); // Réinitialiser le flag de synchronisation
    setCurrentSlot(1); // Réinitialiser le slot aussi
    // On garde currentAdventure pour pouvoir afficher le menu avec l'image
    // currentAdventure sera remplacé lors du prochain initGame
  };

  /**
   * Revenir à l'étape précédente (mode éditeur uniquement)
   */
  const goToPreviousStep = async () => {
    // VÉRIFICATION CRITIQUE : Le mode éditeur DOIT être activé pour revenir en arrière
    if (!editorMode) {
      const error = new Error('Le retour en arrière n\'est autorisé qu\'en mode éditeur. Activez le mode éditeur pour utiliser cette fonctionnalité.');
      console.error('[PlayerContext] Attempted to go back without editor mode:', error);
      throw error;
    }

    if (!gameState || !currentAdventure) {
      const error = new Error('Impossible de revenir en arrière : gameState ou currentAdventure manquant');
      console.warn('[PlayerContext] Cannot go to previous step:', error);
      throw error;
    }

    const recordedPaths = gameState.data.recordedPaths || '';
    const paths = recordedPaths ? recordedPaths.split('-') : [];
    
    if (paths.length < 2) {
      const error = new Error('Impossible de revenir en arrière : pas assez d\'étapes dans l\'historique');
      console.warn('[PlayerContext] Cannot go to previous step:', error);
      throw error;
    }

    // Retirer le dernier step de l'historique
    paths.pop();
    const previousStepNumber = parseInt(paths[paths.length - 1], 10);
    
    if (isNaN(previousStepNumber)) {
      console.error('[PlayerContext] Invalid previous step number');
      return;
    }

    console.log(`[PlayerContext] Going back to step ${previousStepNumber} (from ${gameState.data.currentStepNumber})`);

    setIsLoading(true);
    try {
      // Charger l'étape précédente
      const previousStep = await playerService.loadPlayerStep(
        gameState.uniqueURI,
        previousStepNumber,
      );

      // Mettre à jour le gameState
      const updatedData = {
        ...gameState.data,
        currentStepNumber: previousStepNumber,
        recordedPaths: paths.join('-'),
      };

      const updatedGameState: GameState = {
        ...gameState,
        data: updatedData,
        dirty: true,
        lastUpdate: new Date().toISOString(),
      };

      // Reconstruire l'étape complète avec getGameStep
      const gameStep = await gameLogicService.getGameStep(
        updatedGameState,
        previousStep,
        'cheat',
        {doEventsAndUpdateRecordedPaths: false}, // Ne pas mettre à jour recordedPaths car on l'a déjà fait
      );

      const finalGameState = gameStep.gameState || updatedGameState;
      setGameState(finalGameState);
      gameLogicService.setGameState(finalGameState);
      setCurrentStep(gameStep);

      console.log(`[PlayerContext] Successfully went back to step ${previousStepNumber}`);
    } catch (error) {
      console.error('[PlayerContext] Error going to previous step:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: PlayerContextType = {
    currentStep,
    gameState,
    currentAdventure,
    isLoading,
    isContextReady,
    currentSlot,
    editorMode,
    setEditorMode,
    initGame,
    initTestGame,
    startSlot,
    loadGame,
    choosePath,
    executeAction,
    refreshCurrentStep,
    resetGameState,
    setGameState,
    goToPreviousStep,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

export const usePlayer = (): PlayerContextType => {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    // Log détaillé pour comprendre pourquoi le contexte n'est pas disponible
    console.error('[usePlayer] Context is undefined - this should not happen if component is within PlayerProvider');
    console.error('[usePlayer] Stack trace:', new Error().stack);
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  
  // Log pour debug - voir ce que le hook retourne à chaque rendu
  useEffect(() => {
    console.log(`[usePlayer] Context values - gameState: ${!!context.gameState}, currentStep: ${!!context.currentStep}, isLoading: ${context.isLoading}`);
    if (context.gameState) {
      console.log(`[usePlayer] gameState.uniqueURI: ${context.gameState.uniqueURI}, gameState.slot: ${context.gameState.slot}`);
    }
    if (context.currentStep) {
      console.log(`[usePlayer] currentStep.numero: ${context.currentStep.numero}`);
    }
  });
  
  return context;
};
